import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import {
  TrendChart,
  StatusPieChart,
  SimpleBarChart,
} from "../components/charts";
import {
  useTakebackStats,
  useDailyTakebackCounts,
  useTakebacks,
  useWeightAnalytics,
  useCostAnalytics,
  useGeoAnalytics,
  useCustomers,
} from "../lib/tes-hooks";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";

// --- Mock data generators for demo tenants ---
function generateDailyVolume(
  days: number,
  baseVolume: number,
): Array<{ date: string; count: number }> {
  const data: Array<{ date: string; count: number }> = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayOfWeek = date.getDay();
    const weekendDip = dayOfWeek === 0 || dayOfWeek === 6 ? 0.6 : 1;
    const trend = 1 + ((days - i) / days) * 0.3; // gradual growth
    const noise = 0.7 + Math.random() * 0.6;
    data.push({
      date: date.toISOString().split("T")[0],
      count: Math.round(baseVolume * weekendDip * trend * noise),
    });
  }
  return data;
}

function generateMockStats(period: string) {
  const multiplier =
    period === "7d"
      ? 0.25
      : period === "30d"
        ? 1
        : period === "90d"
          ? 3
          : period === "120d"
            ? 4
            : 8;
  const total = Math.round(1247 * multiplier);
  const completed = Math.round(total * 0.68);
  const inProgress = Math.round(total * 0.12);
  const received = Math.round(total * 0.08);
  const pending = Math.round(total * 0.07);
  const rejected = total - completed - inProgress - received - pending;
  return {
    total,
    totalProducts: Math.round(total * 2.3),
    totalWeight: Math.round(total * 4.2),
    rewardsIssued: Math.round(total * 0.82),
    byStatus: {
      completed,
      in_progress: inProgress,
      received,
      pending_review: pending,
      rejected: Math.max(0, rejected),
    },
  };
}

function generateMockWeightData(period: string) {
  const multiplier =
    period === "7d"
      ? 0.25
      : period === "30d"
        ? 1
        : period === "90d"
          ? 3
          : period === "120d"
            ? 4
            : 8;
  const totalWeight = Math.round(5240 * multiplier);
  const conformWeight = Math.round(totalWeight * 0.78);
  const nonConformWeight = totalWeight - conformWeight;
  return {
    totalWeight,
    conformWeight,
    nonConformWeight,
    avgWeightPerTakeback: 4.2,
    byMethod: {
      in_store_dropoff: {
        weight: Math.round(totalWeight * 0.45),
        count: Math.round(320 * multiplier),
      },
      mail_in: {
        weight: Math.round(totalWeight * 0.3),
        count: Math.round(215 * multiplier),
      },
      partner_location: {
        weight: Math.round(totalWeight * 0.15),
        count: Math.round(108 * multiplier),
      },
      event_collection: {
        weight: Math.round(totalWeight * 0.1),
        count: Math.round(72 * multiplier),
      },
    },
  };
}

function generateMockCostData(period: string) {
  const days =
    period === "7d"
      ? 7
      : period === "30d"
        ? 30
        : period === "90d"
          ? 90
          : period === "120d"
            ? 120
            : 365;
  const multiplier =
    period === "7d"
      ? 0.25
      : period === "30d"
        ? 1
        : period === "90d"
          ? 3
          : period === "120d"
            ? 4
            : 8;
  const totalAwardCost = Math.round(18750 * multiplier);
  return {
    totalAwardCost,
    avgAwardCost: 15.03,
    costPerPound: 3.58,
    byMethod: {
      in_store_dropoff: {
        cost: Math.round(totalAwardCost * 0.4),
        count: Math.round(320 * multiplier),
        avgCost: 14.5,
      },
      mail_in: {
        cost: Math.round(totalAwardCost * 0.35),
        count: Math.round(215 * multiplier),
        avgCost: 16.2,
      },
      partner_location: {
        cost: Math.round(totalAwardCost * 0.15),
        count: Math.round(108 * multiplier),
        avgCost: 13.8,
      },
      event_collection: {
        cost: Math.round(totalAwardCost * 0.1),
        count: Math.round(72 * multiplier),
        avgCost: 12.5,
      },
    },
    dailyTrend: generateDailyVolume(days, 625).map((d) => ({
      date: d.date,
      cost: d.count,
    })),
  };
}

function generateMockGeoData(period: string) {
  const multiplier =
    period === "7d"
      ? 0.25
      : period === "30d"
        ? 1
        : period === "90d"
          ? 3
          : period === "120d"
            ? 4
            : 8;
  const states: Record<
    string,
    { count: number; weight: number; incentive: number }
  > = {
    California: {
      count: Math.round(186 * multiplier),
      weight: Math.round(780 * multiplier),
      incentive: Math.round(2790 * multiplier),
    },
    "New York": {
      count: Math.round(142 * multiplier),
      weight: Math.round(596 * multiplier),
      incentive: Math.round(2130 * multiplier),
    },
    Texas: {
      count: Math.round(118 * multiplier),
      weight: Math.round(495 * multiplier),
      incentive: Math.round(1770 * multiplier),
    },
    Florida: {
      count: Math.round(97 * multiplier),
      weight: Math.round(407 * multiplier),
      incentive: Math.round(1455 * multiplier),
    },
    Illinois: {
      count: Math.round(84 * multiplier),
      weight: Math.round(352 * multiplier),
      incentive: Math.round(1260 * multiplier),
    },
    Pennsylvania: {
      count: Math.round(72 * multiplier),
      weight: Math.round(302 * multiplier),
      incentive: Math.round(1080 * multiplier),
    },
    Ohio: {
      count: Math.round(65 * multiplier),
      weight: Math.round(273 * multiplier),
      incentive: Math.round(975 * multiplier),
    },
    Georgia: {
      count: Math.round(58 * multiplier),
      weight: Math.round(243 * multiplier),
      incentive: Math.round(870 * multiplier),
    },
    Michigan: {
      count: Math.round(52 * multiplier),
      weight: Math.round(218 * multiplier),
      incentive: Math.round(780 * multiplier),
    },
    Washington: {
      count: Math.round(48 * multiplier),
      weight: Math.round(201 * multiplier),
      incentive: Math.round(720 * multiplier),
    },
    Oregon: {
      count: Math.round(43 * multiplier),
      weight: Math.round(180 * multiplier),
      incentive: Math.round(645 * multiplier),
    },
    Colorado: {
      count: Math.round(38 * multiplier),
      weight: Math.round(159 * multiplier),
      incentive: Math.round(570 * multiplier),
    },
  };
  const topStates = Object.entries(states)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 10)
    .map(([state, data]) => ({ state, count: data.count }));
  return { byState: states, topStates };
}

function generateMockCustomers(page: number, pageSize: number) {
  const firstNames = [
    "Alex",
    "Jordan",
    "Taylor",
    "Morgan",
    "Casey",
    "Riley",
    "Drew",
    "Jamie",
    "Sam",
    "Pat",
    "Chris",
    "Robin",
    "Dana",
    "Avery",
    "Quinn",
    "Skyler",
    "Reese",
    "Finley",
    "Sage",
    "Blake",
  ];
  const lastNames = [
    "Johnson",
    "Chen",
    "Patel",
    "Kim",
    "Singh",
    "Martinez",
    "Williams",
    "Brown",
    "Lee",
    "Garcia",
    "Miller",
    "Davis",
    "Rodriguez",
    "Wilson",
    "Anderson",
    "Thomas",
    "Jackson",
    "White",
    "Harris",
    "Clark",
  ];
  const cities = [
    "Portland",
    "New York",
    "Los Angeles",
    "Chicago",
    "Seattle",
    "Austin",
    "Denver",
    "Miami",
    "San Francisco",
    "Boston",
  ];
  const statesList = [
    "OR",
    "NY",
    "CA",
    "IL",
    "WA",
    "TX",
    "CO",
    "FL",
    "CA",
    "MA",
  ];
  const totalCount = 847;
  const items = [];
  const start = page * pageSize;
  for (let i = 0; i < pageSize && start + i < totalCount; i++) {
    const idx = start + i;
    const fn = firstNames[idx % firstNames.length];
    const ln = lastNames[(idx * 7) % lastNames.length];
    const cityIdx = idx % cities.length;
    items.push({
      id: `cust_${String(idx).padStart(4, "0")}`,
      name: fn,
      last_name: ln,
      email: `${fn.toLowerCase()}.${ln.toLowerCase()}@example.com`,
      city: cities[cityIdx],
      state: statesList[cityIdx],
      created_at: Math.floor(Date.now() / 1000) - idx * 86400 * 0.5,
    });
  }
  return {
    items,
    pageInfo: { totalCount, hasNextPage: start + pageSize < totalCount },
  };
}

function generateMockTakebacks() {
  const types = ["Footwear Return", "Apparel Return", "Accessories Return"];
  const statuses = [
    "completed",
    "in_progress",
    "received",
    "pending_review",
    "completed",
  ];
  const items = Array.from({ length: 5 }, (_, i) => ({
    id: `tb_${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`,
    type_name: types[i % types.length],
    status_history: [{ status: statuses[i] }],
    created_at: Math.floor(Date.now() / 1000) - i * 86400 * 2,
  }));
  return { items };
}

function DemoBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full"
      style={{ backgroundColor: "rgba(0,251,169,0.15)", color: "#171717" }}
    >
      Demo Data
    </span>
  );
}

interface Program {
  id: string;
  name: string;
  type: "takeback" | "trade_in" | "resale";
  status: string;
  config: string | Record<string, unknown>;
  external_id: string | null;
}

export const Route = createFileRoute("/programs_/$id_/analytics")({
  component: ProgramAnalytics,
});

type AnalyticsTab =
  | "overview"
  | "volume"
  | "status"
  | "weight"
  | "cost"
  | "geographic"
  | "customers";

function ProgramAnalytics() {
  const params = Route.useParams() as { id: string };
  const id = params.id;
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState<"7d" | "30d" | "90d" | "120d" | "all">(
    "30d",
  );
  const [activeTab, setActiveTab] = useState<AnalyticsTab>("overview");
  const { tenantId } = useTenant();

  useEffect(() => {
    fetchProgram();
  }, [id, tenantId]);

  const fetchProgram = async () => {
    try {
      const response = await tenantFetch(`/api/programs/${id}`);
      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          setProgram(data);
          return;
        }
      }
      // Fallback to demo data
      const demoProgram = getDemoProgram(tenantId, id);
      if (demoProgram) {
        setProgram(demoProgram);
      } else {
        setError("Program not found");
      }
    } catch {
      const demoProgram = getDemoProgram(tenantId, id);
      if (demoProgram) {
        setProgram(demoProgram);
      } else {
        setError("Failed to load program");
      }
    } finally {
      setLoading(false);
    }
  };

  function getDemoProgram(tid: string, pid: string): Program | null {
    if (tid === "arcteryx") {
      const programs: Record<string, Program> = {
        "prg-arc-1": {
          id: "prg-arc-1",
          name: "Arc'teryx ReBIRD",
          type: "trade_in",
          status: "active",
          config: {},
          external_id: "arcteryx-rebird-prod",
        },
        "prg-arc-2": {
          id: "prg-arc-2",
          name: "Arc'teryx Repair Program",
          type: "takeback",
          status: "draft",
          config: {},
          external_id: null,
        },
      };
      return programs[pid] || null;
    }
    const defaultPrograms: Record<string, Program> = {
      "prg-lulu-1": {
        id: "prg-lulu-1",
        name: "Like New Trade-In",
        type: "trade_in",
        status: "active",
        config: {},
        external_id: "lululemon-likenew-prod",
      },
    };
    return defaultPrograms[pid] || null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#00FBA9", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="text-center py-12">
        <h1 className="text-xl font-light mb-2" style={{ color: "#171717" }}>
          {error || "Program not found"}
        </h1>
        <Link to="/programs" className="text-sm" style={{ color: "#00FBA9" }}>
          Back to Programs
        </Link>
      </div>
    );
  }

  const programWithParsedConfig = {
    ...program,
    config:
      typeof program.config === "string"
        ? JSON.parse(program.config)
        : program.config,
  };

  const tabs: { id: AnalyticsTab; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "volume", label: "Volume" },
    { id: "status", label: "Status" },
    { id: "weight", label: "Weight" },
    { id: "cost", label: "Cost & Awards" },
    { id: "geographic", label: "Geographic" },
    { id: "customers", label: "Customers" },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <div
        className="flex items-center gap-2 text-sm mb-6"
        style={{ color: "#A9A9A9" }}
      >
        <Link to="/programs" className="hover:underline">
          Programs
        </Link>
        <span>/</span>
        <Link
          to="/programs/$id"
          params={{ id: program.id }}
          className="hover:underline"
        >
          {program.name}
        </Link>
        <span>/</span>
        <span style={{ color: "#171717" }}>Analytics</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 flex items-center justify-center"
            style={{ backgroundColor: "#00FBA9" }}
          >
            <AnalyticsIcon />
          </div>
          <div>
            <h1 className="text-xl font-light" style={{ color: "#171717" }}>
              {program.name} Analytics
            </h1>
            <p className="text-sm" style={{ color: "#A9A9A9" }}>
              Deep dive into program performance
            </p>
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex gap-1 p-1" style={{ backgroundColor: "#FAFAFA" }}>
          {(["7d", "30d", "90d", "120d", "all"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
              style={
                period === p
                  ? { backgroundColor: "#171717", color: "#FFFFFF" }
                  : { color: "#A9A9A9" }
              }
            >
              {p === "7d"
                ? "7D"
                : p === "30d"
                  ? "30D"
                  : p === "90d"
                    ? "90D"
                    : p === "120d"
                      ? "120D"
                      : "All"}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 mb-6 border-b"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-4 py-3 text-sm font-medium transition-colors relative"
            style={{
              color: activeTab === tab.id ? "#171717" : "#A9A9A9",
            }}
          >
            {tab.label}
            {activeTab === tab.id && (
              <div
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ backgroundColor: "#00FBA9" }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {program.type === "takeback" || program.type === "trade_in" ? (
        <>
          {activeTab === "overview" && (
            <OverviewTab program={programWithParsedConfig} period={period} />
          )}
          {activeTab === "volume" && (
            <VolumeTab program={programWithParsedConfig} period={period} />
          )}
          {activeTab === "status" && (
            <StatusTab program={programWithParsedConfig} period={period} />
          )}
          {activeTab === "weight" && (
            <WeightTab program={programWithParsedConfig} period={period} />
          )}
          {activeTab === "cost" && (
            <CostTab program={programWithParsedConfig} period={period} />
          )}
          {activeTab === "geographic" && (
            <GeoTab program={programWithParsedConfig} period={period} />
          )}
          {activeTab === "customers" && (
            <CustomersTab program={programWithParsedConfig} />
          )}
        </>
      ) : (
        <ResaleAnalytics program={program} period={period} />
      )}
    </div>
  );
}

// Overview Tab - Key metrics summary
function OverviewTab({
  program,
  period,
}: {
  program: Program;
  period: "7d" | "30d" | "90d" | "120d" | "all";
}) {
  const days =
    period === "7d"
      ? 7
      : period === "30d"
        ? 30
        : period === "90d"
          ? 90
          : period === "120d"
            ? 120
            : -1;
  const config = typeof program.config === "object" ? program.config : {};
  const isConfigured = !!(
    program.external_id || (config as Record<string, unknown>).tes_client_id
  );

  const { data: tesStats, isLoading: statsLoading } = useTakebackStats(
    program,
    period,
  );
  const { data: tesDailyCounts, isLoading: countsLoading } =
    useDailyTakebackCounts(program, days);

  const useDemo = !isConfigured;
  const mockStats = useMemo(
    () => (useDemo ? generateMockStats(period) : null),
    [useDemo, period],
  );
  const mockDaily = useMemo(
    () => (useDemo ? generateDailyVolume(days === -1 ? 365 : days, 42) : null),
    [useDemo, days],
  );

  const isLoading = useDemo ? false : statsLoading || countsLoading;
  const stats = useDemo ? mockStats : tesStats;
  const dailyCounts = useDemo ? mockDaily : tesDailyCounts;

  const dailyTotal = dailyCounts?.reduce((sum, d) => sum + d.count, 0) || 0;
  const avgDaily = dailyCounts?.length
    ? Math.round(dailyTotal / dailyCounts.length)
    : 0;
  const completionRate =
    stats?.byStatus?.completed && stats?.total
      ? Math.round((stats.byStatus.completed / stats.total) * 100)
      : 0;
  const avgWeight =
    stats?.total && stats?.totalWeight
      ? Math.round(stats.totalWeight / stats.total)
      : 0;

  return (
    <div className="space-y-6">
      {useDemo && (
        <div className="flex justify-end">
          <DemoBadge />
        </div>
      )}
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Total Takebacks"
          value={isLoading ? "-" : (stats?.total || 0).toLocaleString()}
          icon={<BoxIcon />}
          trend={useDemo ? 12 : null}
        />
        <SummaryCard
          label="Products Received"
          value={isLoading ? "-" : (stats?.totalProducts || 0).toLocaleString()}
          icon={<ProductIcon />}
          trend={useDemo ? 8 : null}
        />
        <SummaryCard
          label="Total Weight"
          value={
            isLoading
              ? "-"
              : `${(stats?.totalWeight || 0).toLocaleString()} lbs`
          }
          icon={<WeightIcon />}
          trend={useDemo ? 15 : null}
        />
        <SummaryCard
          label="Rewards Issued"
          value={isLoading ? "-" : (stats?.rewardsIssued || 0).toLocaleString()}
          icon={<RewardIcon />}
          trend={useDemo ? 5 : null}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Volume Trend
          </h3>
          <div className="h-48">
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <TrendChart data={dailyCounts || []} height={180} showGrid />
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Status Distribution
          </h3>
          {isLoading ? (
            <div className="h-48">
              <LoadingSpinner />
            </div>
          ) : Object.keys(stats?.byStatus || {}).length > 0 ? (
            <StatusPieChart data={stats?.byStatus || {}} height={180} />
          ) : (
            <EmptyState message="No status data available" />
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="card">
        <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
          Key Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricBox
            label="Avg Daily Volume"
            value={isLoading ? "-" : avgDaily.toString()}
            unit="takebacks/day"
          />
          <MetricBox
            label="Completion Rate"
            value={isLoading ? "-" : `${completionRate}%`}
            unit="of total"
          />
          <MetricBox
            label="Avg Weight"
            value={isLoading ? "-" : `${avgWeight}`}
            unit="lbs/takeback"
          />
          <MetricBox
            label="Reward Rate"
            value={
              isLoading
                ? "-"
                : stats?.total
                  ? `${Math.round((stats.rewardsIssued / stats.total) * 100)}%`
                  : "0%"
            }
            unit="with reward"
          />
        </div>
      </div>
    </div>
  );
}

// Volume Tab - Detailed volume analysis
function VolumeTab({
  program,
  period,
}: {
  program: Program;
  period: "7d" | "30d" | "90d" | "120d" | "all";
}) {
  // -1 means fetch all data
  const days =
    period === "7d"
      ? 7
      : period === "30d"
        ? 30
        : period === "90d"
          ? 90
          : period === "120d"
            ? 120
            : -1;
  const { data: tesDailyCounts, isLoading: tesLoading } =
    useDailyTakebackCounts(program, days);

  const config = typeof program.config === "object" ? program.config : {};
  const isConfigured = !!(
    program.external_id || (config as Record<string, unknown>).tes_client_id
  );

  const useDemo = !isConfigured;
  const mockDaily = useMemo(
    () => (useDemo ? generateDailyVolume(days === -1 ? 365 : days, 42) : null),
    [useDemo, days],
  );
  const dailyCounts = useDemo ? mockDaily : tesDailyCounts;
  const isLoading = useDemo ? false : tesLoading;

  // Calculate weekly aggregates
  const weeklyData = dailyCounts ? aggregateByWeek(dailyCounts) : [];

  return (
    <div className="space-y-6">
      {useDemo && (
        <div className="flex justify-end">
          <DemoBadge />
        </div>
      )}
      {/* Daily Volume Chart */}
      <div className="card">
        <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
          Daily Volume
        </h3>
        <div className="h-64">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <TrendChart data={dailyCounts || []} height={240} showGrid />
          )}
        </div>
      </div>

      {/* Weekly Comparison */}
      <div className="card">
        <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
          Weekly Comparison
        </h3>
        <div className="h-48">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <SimpleBarChart
              data={weeklyData.map((w) => ({ name: w.week, value: w.count }))}
              height={180}
            />
          )}
        </div>
      </div>

      {/* Volume Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Peak Day
          </p>
          <p className="text-xl font-light" style={{ color: "#171717" }}>
            {dailyCounts && dailyCounts.length > 0
              ? Math.max(...dailyCounts.map((d) => d.count)).toLocaleString()
              : "-"}
          </p>
          <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
            takebacks
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Average
          </p>
          <p className="text-xl font-light" style={{ color: "#171717" }}>
            {dailyCounts && dailyCounts.length > 0
              ? Math.round(
                  dailyCounts.reduce((sum, d) => sum + d.count, 0) /
                    dailyCounts.length,
                ).toLocaleString()
              : "-"}
          </p>
          <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
            per day
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Total Period
          </p>
          <p className="text-xl font-light" style={{ color: "#171717" }}>
            {dailyCounts && dailyCounts.length > 0
              ? dailyCounts
                  .reduce((sum, d) => sum + d.count, 0)
                  .toLocaleString()
              : "-"}
          </p>
          <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
            takebacks
          </p>
        </div>
      </div>
    </div>
  );
}

// Status Tab - Pipeline and status analysis
function StatusTab({
  program,
  period,
}: {
  program: Program;
  period: "7d" | "30d" | "90d" | "120d" | "all";
}) {
  const { data: tesStats, isLoading: tesStatsLoading } = useTakebackStats(
    program,
    period,
  );
  const { data: tesTakebacks } = useTakebacks(program, { limit: 100 });

  const config = typeof program.config === "object" ? program.config : {};
  const isConfigured = !!(
    program.external_id || (config as Record<string, unknown>).tes_client_id
  );

  const useDemo = !isConfigured;
  const mockStats = useMemo(
    () => (useDemo ? generateMockStats(period) : null),
    [useDemo, period],
  );
  const mockTakebacks = useMemo(
    () => (useDemo ? generateMockTakebacks() : null),
    [useDemo],
  );
  const stats = useDemo ? mockStats : tesStats;
  const takebacks = useDemo ? mockTakebacks : tesTakebacks;
  const isLoading = useDemo ? false : tesStatsLoading;

  // Calculate status breakdown
  const statusData = Object.entries(stats?.byStatus || {})
    .map(([status, count]) => ({
      name: status.replace(/_/g, " "),
      value: count as number,
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6">
      {useDemo && (
        <div className="flex justify-end">
          <DemoBadge />
        </div>
      )}
      {/* Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Status Distribution
          </h3>
          {isLoading ? (
            <div className="h-64">
              <LoadingSpinner />
            </div>
          ) : statusData.length > 0 ? (
            <StatusPieChart data={stats?.byStatus || {}} height={240} />
          ) : (
            <EmptyState message="No status data" />
          )}
        </div>

        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Status Breakdown
          </h3>
          {isLoading ? (
            <div className="h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <SimpleBarChart data={statusData} height={240} horizontal />
          )}
        </div>
      </div>

      {/* Pipeline Stats */}
      <div className="card">
        <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
          Pipeline Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statusData.slice(0, 4).map((status) => (
            <div
              key={status.name}
              className="p-4"
              style={{ backgroundColor: "#FAFAFA" }}
            >
              <p
                className="text-xs capitalize mb-1"
                style={{ color: "#A9A9A9" }}
              >
                {status.name}
              </p>
              <p className="text-2xl font-light" style={{ color: "#171717" }}>
                {status.value.toLocaleString()}
              </p>
              <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
                {stats?.total
                  ? `${Math.round((status.value / stats.total) * 100)}%`
                  : "0%"}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent by Status */}
      <div className="card">
        <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
          Recent Takebacks
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(169,169,169,0.2)" }}>
                <th
                  className="text-left py-2 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  ID
                </th>
                <th
                  className="text-left py-2 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  Type
                </th>
                <th
                  className="text-left py-2 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  Status
                </th>
                <th
                  className="text-left py-2 font-medium"
                  style={{ color: "#A9A9A9" }}
                >
                  Created
                </th>
              </tr>
            </thead>
            <tbody>
              {(takebacks?.items || []).slice(0, 5).map((tb) => {
                const latestStatus = tb.status_history?.length
                  ? tb.status_history[tb.status_history.length - 1]?.status
                  : "unknown";
                return (
                  <tr
                    key={tb.id}
                    style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
                  >
                    <td className="py-2 font-mono text-xs">
                      {tb.id.slice(0, 8)}...
                    </td>
                    <td className="py-2">{tb.type_name}</td>
                    <td className="py-2">
                      <span
                        className="px-2 py-0.5 rounded-full text-xs capitalize"
                        style={{ backgroundColor: "#FAFAFA" }}
                      >
                        {latestStatus.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="py-2" style={{ color: "#A9A9A9" }}>
                      {new Date(tb.created_at * 1000).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Weight Tab - Weight metrics and analysis
function WeightTab({
  program,
  period,
}: {
  program: Program;
  period: "7d" | "30d" | "90d" | "120d" | "all";
}) {
  const { data: tesWeightData, isLoading: tesLoading } = useWeightAnalytics(
    program,
    period,
  );

  const config = typeof program.config === "object" ? program.config : {};
  const isConfigured = !!(
    program.external_id || (config as Record<string, unknown>).tes_client_id
  );

  const useDemo = !isConfigured;
  const mockWeight = useMemo(
    () => (useDemo ? generateMockWeightData(period) : null),
    [useDemo, period],
  );
  const weightData = useDemo ? mockWeight : tesWeightData;
  const isLoading = useDemo ? false : tesLoading;

  // Format method data for chart
  const methodData = Object.entries(weightData?.byMethod || {})
    .map(([method, data]) => ({
      name: method.replace(/_/g, " "),
      value: Math.round(data.weight),
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6">
      {useDemo && (
        <div className="flex justify-end">
          <DemoBadge />
        </div>
      )}
      {/* Weight KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Total Weight
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading
              ? "-"
              : `${Math.round(weightData?.totalWeight || 0).toLocaleString()} lbs`}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Conform Weight
          </p>
          <p className="text-2xl font-light" style={{ color: "#00FBA9" }}>
            {isLoading
              ? "-"
              : `${Math.round(weightData?.conformWeight || 0).toLocaleString()} lbs`}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Non-Conform Weight
          </p>
          <p className="text-2xl font-light" style={{ color: "#F5A623" }}>
            {isLoading
              ? "-"
              : `${Math.round(weightData?.nonConformWeight || 0).toLocaleString()} lbs`}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Avg per Takeback
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading
              ? "-"
              : `${(weightData?.avgWeightPerTakeback || 0).toFixed(1)} lbs`}
          </p>
        </div>
      </div>

      {/* Weight by Method */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Weight by Return Method
          </h3>
          {isLoading ? (
            <div className="h-64">
              <LoadingSpinner />
            </div>
          ) : methodData.length > 0 ? (
            <SimpleBarChart data={methodData} height={240} horizontal />
          ) : (
            <EmptyState message="No weight data" />
          )}
        </div>

        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Weight Breakdown by Method
          </h3>
          <div className="space-y-3">
            {Object.entries(weightData?.byMethod || {})
              .slice(0, 5)
              .map(([method, data]) => (
                <div
                  key={method}
                  className="flex items-center justify-between py-2"
                  style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
                >
                  <span
                    className="text-sm capitalize"
                    style={{ color: "#171717" }}
                  >
                    {method.replace(/_/g, " ")}
                  </span>
                  <div className="text-right">
                    <span
                      className="text-sm font-medium"
                      style={{ color: "#171717" }}
                    >
                      {Math.round(data.weight).toLocaleString()} lbs
                    </span>
                    <span className="text-xs ml-2" style={{ color: "#A9A9A9" }}>
                      ({data.count} cases)
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Conform Rate */}
      <div className="card">
        <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
          Quality Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(() => {
            // Calculate rates using only processed weights (conform + non-conform)
            const processedWeight =
              (weightData?.conformWeight || 0) +
              (weightData?.nonConformWeight || 0);
            const conformRate =
              processedWeight > 0
                ? Math.round(
                    ((weightData?.conformWeight || 0) / processedWeight) * 100,
                  )
                : 0;
            const nonConformRate =
              processedWeight > 0
                ? Math.round(
                    ((weightData?.nonConformWeight || 0) / processedWeight) *
                      100,
                  )
                : 0;

            return (
              <>
                <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
                  <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
                    Conform Rate
                  </p>
                  <p
                    className="text-2xl font-light"
                    style={{ color: "#00FBA9" }}
                  >
                    {isLoading ? "-" : `${conformRate}%`}
                  </p>
                  <ProgressBar value={conformRate} />
                </div>
                <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
                  <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
                    Non-Conform Rate
                  </p>
                  <p
                    className="text-2xl font-light"
                    style={{ color: "#F5A623" }}
                  >
                    {isLoading ? "-" : `${nonConformRate}%`}
                  </p>
                  <ProgressBar value={nonConformRate} />
                </div>
              </>
            );
          })()}
          <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              Return Methods
            </p>
            <p className="text-2xl font-light" style={{ color: "#171717" }}>
              {Object.keys(weightData?.byMethod || {}).length}
            </p>
            <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
              active methods
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Cost Tab - Award and cost analysis
function CostTab({
  program,
  period,
}: {
  program: Program;
  period: "7d" | "30d" | "90d" | "120d" | "all";
}) {
  const { data: tesCostData, isLoading: tesLoading } = useCostAnalytics(
    program,
    period,
  );

  const config = typeof program.config === "object" ? program.config : {};
  const isConfigured = !!(
    program.external_id || (config as Record<string, unknown>).tes_client_id
  );

  const useDemo = !isConfigured;
  const mockCost = useMemo(
    () => (useDemo ? generateMockCostData(period) : null),
    [useDemo, period],
  );
  const costData = useDemo ? mockCost : tesCostData;
  const isLoading = useDemo ? false : tesLoading;

  // Format method data for chart
  const methodData = Object.entries(costData?.byMethod || {})
    .map(([method, data]) => ({
      name: method.replace(/_/g, " "),
      value: Math.round(data.cost),
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6">
      {useDemo && (
        <div className="flex justify-end">
          <DemoBadge />
        </div>
      )}
      {/* Cost KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Total Award Cost
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading
              ? "-"
              : `$${Math.round(costData?.totalAwardCost || 0).toLocaleString()}`}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Avg Award Cost
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading ? "-" : `$${(costData?.avgAwardCost || 0).toFixed(2)}`}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Cost per Pound
          </p>
          <p className="text-2xl font-light" style={{ color: "#00FBA9" }}>
            {isLoading ? "-" : `$${(costData?.costPerPound || 0).toFixed(2)}`}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Return Methods
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {Object.keys(costData?.byMethod || {}).length}
          </p>
        </div>
      </div>

      {/* Cost Trend Chart */}
      <div className="card">
        <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
          Daily Award Costs
        </h3>
        <div className="h-64">
          {isLoading ? (
            <LoadingSpinner />
          ) : costData?.dailyTrend && costData.dailyTrend.length > 0 ? (
            <TrendChart
              data={costData.dailyTrend.map((d) => ({
                date: d.date,
                count: Math.round(d.cost),
              }))}
              height={240}
              showGrid
              color="#F5A623"
            />
          ) : (
            <EmptyState message="No cost trend data" />
          )}
        </div>
      </div>

      {/* Cost by Method */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Cost by Return Method
          </h3>
          {isLoading ? (
            <div className="h-64">
              <LoadingSpinner />
            </div>
          ) : methodData.length > 0 ? (
            <SimpleBarChart
              data={methodData}
              height={240}
              horizontal
              color="#F5A623"
            />
          ) : (
            <EmptyState message="No cost data" />
          )}
        </div>

        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Cost Breakdown by Method
          </h3>
          <div className="space-y-3">
            {Object.entries(costData?.byMethod || {})
              .slice(0, 5)
              .map(([method, data]) => (
                <div
                  key={method}
                  className="flex items-center justify-between py-2"
                  style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
                >
                  <span
                    className="text-sm capitalize"
                    style={{ color: "#171717" }}
                  >
                    {method.replace(/_/g, " ")}
                  </span>
                  <div className="text-right">
                    <span
                      className="text-sm font-medium"
                      style={{ color: "#171717" }}
                    >
                      ${Math.round(data.cost).toLocaleString()}
                    </span>
                    <span className="text-xs ml-2" style={{ color: "#A9A9A9" }}>
                      avg ${data.avgCost.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Geographic Tab - Location-based analytics
function GeoTab({
  program,
  period,
}: {
  program: Program;
  period: "7d" | "30d" | "90d" | "120d" | "all";
}) {
  const { data: tesGeoData, isLoading: tesLoading } = useGeoAnalytics(
    program,
    period,
  );

  const config = typeof program.config === "object" ? program.config : {};
  const isConfigured = !!(
    program.external_id || (config as Record<string, unknown>).tes_client_id
  );

  const useDemo = !isConfigured;
  const mockGeo = useMemo(
    () => (useDemo ? generateMockGeoData(period) : null),
    [useDemo, period],
  );
  const geoData = useDemo ? mockGeo : tesGeoData;
  const isLoading = useDemo ? false : tesLoading;

  // Format data for chart
  const stateData = (geoData?.topStates || []).map((s) => ({
    name: s.state,
    value: s.count,
  }));

  return (
    <div className="space-y-6">
      {useDemo && (
        <div className="flex justify-end">
          <DemoBadge />
        </div>
      )}
      {/* Geographic KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            States Covered
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading ? "-" : Object.keys(geoData?.byState || {}).length}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Top State
          </p>
          <p className="text-2xl font-light" style={{ color: "#00FBA9" }}>
            {isLoading ? "-" : geoData?.topStates?.[0]?.state || "N/A"}
          </p>
          <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
            {geoData?.topStates?.[0]?.count || 0} takebacks
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Total Weight
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading
              ? "-"
              : `${Math.round(
                  Object.values(geoData?.byState || {}).reduce(
                    (sum, s) => sum + s.weight,
                    0,
                  ),
                ).toLocaleString()} lbs`}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Total Incentives
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading
              ? "-"
              : `$${Math.round(
                  Object.values(geoData?.byState || {}).reduce(
                    (sum, s) => sum + s.incentive,
                    0,
                  ),
                ).toLocaleString()}`}
          </p>
        </div>
      </div>

      {/* Top States Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Top 10 States
          </h3>
          {isLoading ? (
            <div className="h-64">
              <LoadingSpinner />
            </div>
          ) : stateData.length > 0 ? (
            <SimpleBarChart
              data={stateData}
              height={280}
              horizontal
              color="#4A90D9"
            />
          ) : (
            <EmptyState message="No geographic data" />
          )}
        </div>

        {/* State Table */}
        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            State Details
          </h3>
          <div className="overflow-y-auto" style={{ maxHeight: "280px" }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(169,169,169,0.2)" }}>
                  <th
                    className="text-left py-2 font-medium sticky top-0 bg-white"
                    style={{ color: "#A9A9A9" }}
                  >
                    State
                  </th>
                  <th
                    className="text-right py-2 font-medium sticky top-0 bg-white"
                    style={{ color: "#A9A9A9" }}
                  >
                    Cases
                  </th>
                  <th
                    className="text-right py-2 font-medium sticky top-0 bg-white"
                    style={{ color: "#A9A9A9" }}
                  >
                    Weight
                  </th>
                  <th
                    className="text-right py-2 font-medium sticky top-0 bg-white"
                    style={{ color: "#A9A9A9" }}
                  >
                    Incentive
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(geoData?.byState || {})
                  .sort((a, b) => b[1].count - a[1].count)
                  .slice(0, 15)
                  .map(([state, data]) => (
                    <tr
                      key={state}
                      style={{
                        borderBottom: "1px solid rgba(169,169,169,0.1)",
                      }}
                    >
                      <td className="py-2" style={{ color: "#171717" }}>
                        {state}
                      </td>
                      <td
                        className="py-2 text-right"
                        style={{ color: "#171717" }}
                      >
                        {data.count.toLocaleString()}
                      </td>
                      <td
                        className="py-2 text-right"
                        style={{ color: "#A9A9A9" }}
                      >
                        {Math.round(data.weight).toLocaleString()}
                      </td>
                      <td
                        className="py-2 text-right"
                        style={{ color: "#A9A9A9" }}
                      >
                        ${Math.round(data.incentive).toLocaleString()}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Customers Tab - Customer analytics
function CustomersTab({ program }: { program: Program }) {
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const { data: tesCustomersData, isLoading: tesLoading } = useCustomers(
    program,
    {
      limit: pageSize,
      offset: page * pageSize,
    },
  );

  const config = typeof program.config === "object" ? program.config : {};
  const isConfigured = !!(
    program.external_id || (config as Record<string, unknown>).tes_client_id
  );

  const useDemo = !isConfigured;
  const mockCustomerData = useMemo(
    () => (useDemo ? generateMockCustomers(page, pageSize) : null),
    [useDemo, page, pageSize],
  );
  const customersData = useDemo ? mockCustomerData : tesCustomersData;
  const isLoading = useDemo ? false : tesLoading;

  const customers = customersData?.items || [];
  const totalCustomers = customersData?.pageInfo?.totalCount || 0;
  const totalPages = Math.ceil(totalCustomers / pageSize);

  return (
    <div className="space-y-6">
      {useDemo && (
        <div className="flex justify-end">
          <DemoBadge />
        </div>
      )}
      {/* Customer KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Total Customers
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading ? "-" : totalCustomers.toLocaleString()}
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Avg per Customer
          </p>
          <p className="text-2xl font-light" style={{ color: "#00FBA9" }}>
            {isLoading ? "-" : totalCustomers > 0 ? "1.4" : "0"}
          </p>
          <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
            takebacks
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Email Capture Rate
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading ? "-" : "94%"}
          </p>
          <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
            of customers
          </p>
        </div>
        <div className="card">
          <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
            Profile Complete
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {isLoading ? "-" : "87%"}
          </p>
          <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
            with location
          </p>
        </div>
      </div>

      {/* Customer Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium" style={{ color: "#171717" }}>
            Customers
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 rounded text-sm"
              style={{
                backgroundColor: page === 0 ? "#FAFAFA" : "#171717",
                color: page === 0 ? "#A9A9A9" : "#FFFFFF",
                cursor: page === 0 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            <span className="text-sm" style={{ color: "#A9A9A9" }}>
              Page {page + 1} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded text-sm"
              style={{
                backgroundColor: page >= totalPages - 1 ? "#FAFAFA" : "#171717",
                color: page >= totalPages - 1 ? "#A9A9A9" : "#FFFFFF",
                cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="h-64 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : customers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(169,169,169,0.2)" }}>
                  <th
                    className="text-left py-2 font-medium"
                    style={{ color: "#A9A9A9" }}
                  >
                    Name
                  </th>
                  <th
                    className="text-left py-2 font-medium"
                    style={{ color: "#A9A9A9" }}
                  >
                    Email
                  </th>
                  <th
                    className="text-left py-2 font-medium"
                    style={{ color: "#A9A9A9" }}
                  >
                    Location
                  </th>
                  <th
                    className="text-left py-2 font-medium"
                    style={{ color: "#A9A9A9" }}
                  >
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    style={{ borderBottom: "1px solid rgba(169,169,169,0.1)" }}
                  >
                    <td className="py-3" style={{ color: "#171717" }}>
                      {customer.name || "-"} {customer.last_name || ""}
                    </td>
                    <td className="py-3" style={{ color: "#A9A9A9" }}>
                      {customer.email || "-"}
                    </td>
                    <td className="py-3" style={{ color: "#A9A9A9" }}>
                      {[customer.city, customer.state]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </td>
                    <td className="py-3" style={{ color: "#A9A9A9" }}>
                      {new Date(
                        customer.created_at * 1000,
                      ).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No customers found" />
        )}
      </div>
    </div>
  );
}

// Helper Components
function SummaryCard({
  label,
  value,
  icon,
  trend,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  trend: number | null;
}) {
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <p className="text-xs" style={{ color: "#A9A9A9" }}>
          {label}
        </p>
        <span style={{ color: "#A9A9A9" }}>{icon}</span>
      </div>
      <p className="text-2xl font-light mt-2" style={{ color: "#171717" }}>
        {value}
      </p>
      {trend !== null && (
        <p
          className="text-xs mt-1"
          style={{
            color: trend > 0 ? "#00FBA9" : trend < 0 ? "#FF4C00" : "#A9A9A9",
          }}
        >
          {trend > 0 ? "↑" : trend < 0 ? "↓" : "→"} {Math.abs(trend)}% vs last
          period
        </p>
      )}
    </div>
  );
}

function MetricBox({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit: string;
}) {
  return (
    <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
      <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
        {label}
      </p>
      <p className="text-xl font-light" style={{ color: "#171717" }}>
        {value}
      </p>
      <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
        {unit}
      </p>
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div
      className="h-1 mt-2 rounded-full overflow-hidden"
      style={{ backgroundColor: "#FAFAFA" }}
    >
      <div
        className="h-full rounded-full transition-all"
        style={{
          width: `${Math.min(100, value)}%`,
          backgroundColor:
            value >= 80 ? "#00FBA9" : value >= 50 ? "#F5A623" : "#FF4C00",
        }}
      />
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="h-full flex items-center justify-center">
      <div
        className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: "#00FBA9", borderTopColor: "transparent" }}
      />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div
      className="h-48 flex items-center justify-center"
      style={{ color: "#A9A9A9" }}
    >
      {message}
    </div>
  );
}

// Resale Analytics - Full implementation
function ResaleAnalytics({
  program,
  period,
}: {
  program: Program;
  period: "7d" | "30d" | "90d" | "120d" | "all";
}) {
  const [analytics, setAnalytics] = useState<{
    source?: string;
    shopifyStore?: string;
    metrics?: {
      totalListings: number;
      activeListings: number;
      draftListings?: number;
      archivedListings?: number;
      fromShopify?: number;
      avgPrice?: number;
      totalValue?: number;
      totalSales: number;
      totalOrders: number;
      gmv: number;
      conversionRate: number;
      itemsSold: number;
      itemsShipped: number;
      itemsDelivered: number;
      dailyEvents?: Array<{ date: string; events: number }>;
      statusBreakdown?: Record<string, number>;
      byBrand?: Record<string, number>;
      priceDistribution?: Record<string, number>;
    };
    error?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `/api/programs/${program.id}/analytics?period=${period}`,
        );
        const data = await response.json();
        setAnalytics(data.analytics);
      } catch (error) {
        console.error("Failed to fetch resale analytics:", error);
        setAnalytics({ error: "Failed to load analytics" });
      } finally {
        setIsLoading(false);
      }
    };
    fetchAnalytics();
  }, [program.id, period]);

  const metrics = analytics?.metrics;
  const dailyData =
    metrics?.dailyEvents?.map((d) => ({ date: d.date, count: d.events })) || [];
  const isShopify = analytics?.source === "shopify";

  if (analytics?.error) {
    return (
      <div className="card text-center py-12">
        <p className="text-sm" style={{ color: "#FF4C00" }}>
          {analytics.error}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Source indicator */}
      {isShopify && analytics?.shopifyStore && (
        <div
          className="flex items-center gap-2 text-sm"
          style={{ color: "#A9A9A9" }}
        >
          <span>📦</span>
          <span>
            Data from Shopify store:{" "}
            <strong style={{ color: "#171717" }}>
              {analytics.shopifyStore}
            </strong>
          </span>
        </div>
      )}

      {/* Resale KPIs - Shopify variant */}
      {isShopify ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              Total Products
            </p>
            <p className="text-2xl font-light" style={{ color: "#00FBA9" }}>
              {isLoading ? "-" : (metrics?.totalListings || 0).toLocaleString()}
            </p>
            <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
              in catalog
            </p>
          </div>
          <div className="card">
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              Active Products
            </p>
            <p className="text-2xl font-light" style={{ color: "#171717" }}>
              {isLoading
                ? "-"
                : (metrics?.activeListings || 0).toLocaleString()}
            </p>
          </div>
          <div className="card">
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              Avg Price
            </p>
            <p className="text-2xl font-light" style={{ color: "#171717" }}>
              {isLoading
                ? "-"
                : `$${(metrics?.avgPrice || 0).toLocaleString()}`}
            </p>
          </div>
          <div className="card">
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              Catalog Value
            </p>
            <p className="text-2xl font-light" style={{ color: "#171717" }}>
              {isLoading
                ? "-"
                : `$${(metrics?.totalValue || 0).toLocaleString()}`}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              GMV
            </p>
            <p className="text-2xl font-light" style={{ color: "#00FBA9" }}>
              {isLoading ? "-" : `$${(metrics?.gmv || 0).toLocaleString()}`}
            </p>
            <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
              gross merchandise value
            </p>
          </div>
          <div className="card">
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              Conversion Rate
            </p>
            <p className="text-2xl font-light" style={{ color: "#171717" }}>
              {isLoading ? "-" : `${metrics?.conversionRate || 0}%`}
            </p>
            <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
              listings to sales
            </p>
          </div>
          <div className="card">
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              Active Listings
            </p>
            <p className="text-2xl font-light" style={{ color: "#171717" }}>
              {isLoading
                ? "-"
                : (metrics?.activeListings || 0).toLocaleString()}
            </p>
          </div>
          <div className="card">
            <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
              Total Sales
            </p>
            <p className="text-2xl font-light" style={{ color: "#171717" }}>
              {isLoading ? "-" : (metrics?.totalSales || 0).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Event Activity
          </h3>
          <div className="h-48">
            {isLoading ? (
              <LoadingSpinner />
            ) : dailyData.length > 0 ? (
              <TrendChart
                data={dailyData}
                height={180}
                showGrid
                color="#4A90D9"
              />
            ) : (
              <EmptyState message="No event data" />
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Item Status
          </h3>
          {isLoading ? (
            <div className="h-48">
              <LoadingSpinner />
            </div>
          ) : metrics?.statusBreakdown &&
            Object.keys(metrics.statusBreakdown).length > 0 ? (
            <StatusPieChart data={metrics.statusBreakdown} height={180} />
          ) : (
            <EmptyState message="No status data" />
          )}
        </div>
      </div>

      {/* Shopify-specific: Brand & Price Distribution */}
      {isShopify && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Brand */}
          {metrics?.byBrand && Object.keys(metrics.byBrand).length > 0 && (
            <div className="card">
              <h3
                className="text-sm font-medium mb-4"
                style={{ color: "#171717" }}
              >
                Products by Brand
              </h3>
              <div className="space-y-2">
                {Object.entries(metrics.byBrand)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([brand, count]) => (
                    <div
                      key={brand}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm" style={{ color: "#171717" }}>
                        {brand}
                      </span>
                      <span
                        className="text-sm font-medium"
                        style={{ color: "#00FBA9" }}
                      >
                        {count}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Price Distribution */}
          {metrics?.priceDistribution &&
            Object.keys(metrics.priceDistribution).length > 0 && (
              <div className="card">
                <h3
                  className="text-sm font-medium mb-4"
                  style={{ color: "#171717" }}
                >
                  Price Distribution
                </h3>
                <SimpleBarChart
                  data={Object.entries(metrics.priceDistribution).map(
                    ([range, count]) => ({
                      name: range,
                      value: count,
                    }),
                  )}
                  height={200}
                  horizontal
                />
              </div>
            )}
        </div>
      )}

      {/* Pipeline Stats - only for non-Shopify */}
      {!isShopify && (
        <div className="card">
          <h3 className="text-sm font-medium mb-4" style={{ color: "#171717" }}>
            Sales Pipeline
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
              <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
                Total Listings
              </p>
              <p className="text-xl font-light" style={{ color: "#171717" }}>
                {isLoading
                  ? "-"
                  : (metrics?.totalListings || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
              <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
                Orders
              </p>
              <p className="text-xl font-light" style={{ color: "#171717" }}>
                {isLoading ? "-" : (metrics?.totalOrders || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
              <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
                Sold
              </p>
              <p className="text-xl font-light" style={{ color: "#00FBA9" }}>
                {isLoading ? "-" : (metrics?.itemsSold || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
              <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
                Shipped
              </p>
              <p className="text-xl font-light" style={{ color: "#4A90D9" }}>
                {isLoading
                  ? "-"
                  : (metrics?.itemsShipped || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4" style={{ backgroundColor: "#FAFAFA" }}>
              <p className="text-xs mb-1" style={{ color: "#A9A9A9" }}>
                Delivered
              </p>
              <p className="text-xl font-light" style={{ color: "#7B61FF" }}>
                {isLoading
                  ? "-"
                  : (metrics?.itemsDelivered || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Utility functions
function aggregateByWeek(dailyData: Array<{ date: string; count: number }>) {
  const weeks: Record<string, number> = {};
  dailyData.forEach((day) => {
    const date = new Date(day.date);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    const weekKey = `W${Math.ceil((weekStart.getTime() - new Date(weekStart.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000))}`;
    weeks[weekKey] = (weeks[weekKey] || 0) + day.count;
  });
  return Object.entries(weeks).map(([week, count]) => ({ week, count }));
}

// Icons
function AnalyticsIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#171717"
      strokeWidth="1.5"
    >
      <path
        d="M9 19V13M12 19V9M15 19V15M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M20 7L12 3L4 7M20 7L12 11M20 7V17L12 21M12 11L4 7M12 11V21M4 7V17L12 21"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProductIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function WeightIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3V21M3 12H21" strokeLinecap="round" />
    </svg>
  );
}

function RewardIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
